
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Users, 
  FileText, 
  MessageSquare, 
  Star,
  TrendingUp,
  Activity,
  Calendar,
  Download
} from 'lucide-react';
import { 
  usePlatformStats, 
  useUserGrowthAnalytics, 
  useContentAnalytics,
  useTopContributors,
  usePopularNodes
} from '@/hooks/useAnalytics';
import { format } from 'date-fns';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const AnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState('30');
  const { data: platformStats, isLoading: statsLoading } = usePlatformStats();
  const { data: userGrowth, isLoading: userGrowthLoading } = useUserGrowthAnalytics(parseInt(timeRange));
  const { data: contentAnalytics, isLoading: contentLoading } = useContentAnalytics(parseInt(timeRange));
  const { data: topContributors, isLoading: contributorsLoading } = useTopContributors();
  const { data: popularNodes, isLoading: nodesLoading } = usePopularNodes();

  const exportData = () => {
    // Simple CSV export functionality
    const csvData = userGrowth?.map(row => 
      `${row.date_created},${row.new_users},${row.cumulative_users}`
    ).join('\n');
    
    const blob = new Blob([`Date,New Users,Total Users\n${csvData}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (statsLoading || userGrowthLoading || contentLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-20 bg-white rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="h-64 bg-white rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-black">Platform insights and metrics</p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportData} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-black" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{platformStats?.total_users || 0}</div>
            <p className="text-xs text-black">
              {platformStats?.active_users_today || 0} active today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published Content</CardTitle>
            <FileText className="h-4 w-4 text-black" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{platformStats?.published_content || 0}</div>
            <p className="text-xs text-black">
              {platformStats?.pending_review || 0} pending review
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Comments</CardTitle>
            <MessageSquare className="h-4 w-4 text-black" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{platformStats?.total_comments || 0}</div>
            <p className="text-xs text-black">
              Community discussions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-black" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{platformStats?.avg_rating || 0}</div>
            <p className="text-xs text-black">
              From {platformStats?.total_ratings || 0} ratings
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="growth" className="space-y-4">
        <TabsList>
          <TabsTrigger value="growth">User Growth</TabsTrigger>
          <TabsTrigger value="content">Content Analytics</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="contributors">Top Contributors</TabsTrigger>
          <TabsTrigger value="popular">Popular Content</TabsTrigger>
        </TabsList>

        <TabsContent value="growth">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                User Growth Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date_created" 
                    tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="new_users" 
                    stroke="#8884d8" 
                    name="New Users"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cumulative_users" 
                    stroke="#82ca9d" 
                    name="Total Users"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Content Creation & Publishing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={contentAnalytics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date_created" 
                    tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                  />
                  <YAxis />
                  <Tooltip 
                    labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                  />
                  <Bar dataKey="content_created" fill="#8884d8" name="Created" />
                  <Bar dataKey="content_published" fill="#82ca9d" name="Published" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Daily Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Active Today</span>
                    <span className="font-bold">{platformStats?.active_users_today}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Active This Week</span>
                    <span className="font-bold">{platformStats?.active_users_week}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Content Created Today</span>
                    <span className="font-bold">{platformStats?.content_created_today}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Content Published Today</span>
                    <span className="font-bold">{platformStats?.content_published_today}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Quick Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">
                      {Math.round(((platformStats?.published_content || 0) / (platformStats?.total_content || 1)) * 100)}%
                    </div>
                    <div className="text-sm text-black">Publication Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {Math.round(((platformStats?.active_users_week || 0) / (platformStats?.total_users || 1)) * 100)}%
                    </div>
                    <div className="text-sm text-black">Weekly Engagement</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contributors">
          <Card>
            <CardHeader>
              <CardTitle>Top Contributors</CardTitle>
            </CardHeader>
            <CardContent>
              {contributorsLoading ? (
                <div className="animate-pulse space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-white rounded"></div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {topContributors?.map((contributor, index) => (
                    <div key={contributor.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">
                          #{index + 1}
                        </div>
                        <div>
                          <div className="font-medium">{contributor.full_name || contributor.username}</div>
                          <div className="text-sm text-black">@{contributor.username}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{contributor.content_count} articles</div>
                        <div className="text-sm text-black">
                          {contributor.published_count} published • ⭐ {contributor.avg_rating}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="popular">
          <Card>
            <CardHeader>
              <CardTitle>Most Popular Content</CardTitle>
            </CardHeader>
            <CardContent>
              {nodesLoading ? (
                <div className="animate-pulse space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-16 bg-white rounded"></div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {popularNodes?.map((node, index) => (
                    <div key={node.node_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center font-bold">
                          #{index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{node.title}</div>
                          <div className="text-sm text-black">
                            by @{node.author_username} • {node.discipline_name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">{node.view_count} views</div>
                        <div className="text-sm text-black">
                          ⭐ {node.avg_rating} • {node.comment_count} comments
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;

